import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { engagementService, timeEntryService } from '../services/api';

export default function EngagementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [engagement, setEngagement] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentMessage, setAttachmentMessage] = useState({ type: '', text: '' });

  // Time entry form state
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [timeFormData, setTimeFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: '',
    billable: true,
  });
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [savingTime, setSavingTime] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [engagementData, timeEntriesData] = await Promise.all([
        engagementService.getById(id),
        timeEntryService.getByEngagement(id),
      ]);
      setEngagement(engagementData);
      setTimeEntries(timeEntriesData);

      // Load attachments
      try {
        const atts = await engagementService.getAttachments(id);
        setAttachments(atts);
      } catch (attErr) {
        console.error('Error loading attachments:', attErr);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load engagement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this engagement? This action cannot be undone.')) {
      return;
    }

    try {
      await engagementService.delete(id);
      navigate('/engagements');
    } catch (err) {
      console.error('Error deleting engagement:', err);
      setError('Failed to delete engagement');
    }
  };

  // Time entry handlers
  const handleTimeFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTimeFormData({
      ...timeFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleTimeFormSubmit = async (e) => {
    e.preventDefault();
    setSavingTime(true);
    setError('');

    try {
      if (editingTimeEntry) {
        await timeEntryService.update(editingTimeEntry.id, {
          date: timeFormData.date,
          hours: parseFloat(timeFormData.hours),
          description: timeFormData.description,
          billable: timeFormData.billable,
        });
      } else {
        await timeEntryService.create({
          engagementId: parseInt(id),
          date: timeFormData.date,
          hours: parseFloat(timeFormData.hours),
          description: timeFormData.description,
          billable: timeFormData.billable,
        });
      }

      // Reload time entries
      const updatedEntries = await timeEntryService.getByEngagement(id);
      setTimeEntries(updatedEntries);

      // Reset form
      setShowTimeForm(false);
      setEditingTimeEntry(null);
      setTimeFormData({
        date: new Date().toISOString().split('T')[0],
        hours: '',
        description: '',
        billable: true,
      });
    } catch (err) {
      console.error('Error saving time entry:', err);
      setError('Failed to save time entry');
    } finally {
      setSavingTime(false);
    }
  };

  const handleEditTimeEntry = (entry) => {
    setEditingTimeEntry(entry);
    setTimeFormData({
      date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : '',
      hours: entry.hours.toString(),
      description: entry.description,
      billable: entry.billable,
    });
    setShowTimeForm(true);
  };

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Delete this time entry?')) return;

    try {
      await timeEntryService.delete(entryId);
      setTimeEntries(timeEntries.filter((e) => e.id !== entryId));
    } catch (err) {
      console.error('Error deleting time entry:', err);
      setError(err.response?.data?.message || 'Failed to delete time entry');
    }
  };

  const cancelTimeForm = () => {
    setShowTimeForm(false);
    setEditingTimeEntry(null);
    setTimeFormData({
      date: new Date().toISOString().split('T')[0],
      hours: '',
      description: '',
      billable: true,
    });
  };

  // Attachment handlers
  const uploadAbortRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > 10 * 1024 * 1024) {
      setAttachmentMessage({ type: 'error', text: 'File exceeds the 10MB size limit.' });
      return;
    }

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;

    setUploading(true);
    setAttachmentMessage({ type: '', text: '' });

    try {
      const newAttachment = await engagementService.uploadAttachment(id, file, {
        signal: abortController.signal,
      });
      setAttachments([...attachments, newAttachment]);
      setAttachmentMessage({ type: 'success', text: `"${file.name}" uploaded successfully.` });
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setAttachmentMessage({ type: 'error', text: 'Upload timed out. Please try again.' });
      } else if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        setAttachmentMessage({ type: 'error', text: 'Upload canceled.' });
      } else {
        console.error('Error uploading file:', error);
        const errMsg = error.response?.data?.message || error.response?.data || 'Error uploading file.';
        setAttachmentMessage({ type: 'error', text: typeof errMsg === 'string' ? errMsg : 'Error uploading file.' });
      }
    } finally {
      uploadAbortRef.current = null;
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
  };

  const handleDeleteAttachment = async (attachmentId, filename) => {
    if (!confirm(`Delete attachment "${filename}"? This cannot be undone.`)) return;

    try {
      await engagementService.deleteAttachment(id, attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      setAttachmentMessage({ type: 'success', text: `"${filename}" deleted.` });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setAttachmentMessage({ type: 'error', text: 'Error deleting attachment.' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      paused: 'badge-warning',
      completed: 'badge-info',
      cancelled: 'badge-danger',
    };
    return badges[status] || 'badge-gray';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate time totals
  const totalHours = timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const billableHours = timeEntries.filter((e) => e.billable).reduce((sum, e) => sum + (e.hours || 0), 0);
  const unbilledHours = timeEntries.filter((e) => e.billable && !e.billed).reduce((sum, e) => sum + (e.hours || 0), 0);
  const unbilledAmount = timeEntries
    .filter((e) => e.billable && !e.billed)
    .reduce((sum, e) => sum + (e.hours || 0) * (e.rate || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">Engagement not found</p>
        <Link to="/engagements" className="text-accent hover:underline mt-4 inline-block">
          Back to Engagements
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Engagement: {engagement.title} | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/engagements" className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{engagement.title}</h1>
              {engagement.client && (
                <p className="text-sm text-gray-500">
                  <Link to={`/clients/${engagement.client.id}`} className="hover:text-accent">
                    {engagement.client.company || engagement.client.name}
                  </Link>
                </p>
              )}
            </div>
            <span className={`badge ${getStatusBadge(engagement.status)}`}>
              {engagement.status}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <Link to={`/engagements/${id}/edit`} className="btn btn-secondary">
              Edit
            </Link>
            <button onClick={handleDelete} className="btn btn-danger">
              Delete
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Scope of Work */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Scope of Work</h2>
              {engagement.description ? (
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: engagement.description }}
                />
              ) : (
                <p className="text-gray-400 italic">No description provided</p>
              )}
            </div>

            {/* Notes */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Internal Notes</h2>
              <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700 min-h-[80px]">
                {engagement.notes || <span className="text-gray-400 italic">No notes</span>}
              </div>
            </div>

            {/* Attachments */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">
                Attachments
                {attachments.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({attachments.length} file{attachments.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h2>

              {attachmentMessage.text && (
                <div
                  className={`p-3 rounded-lg mb-4 text-sm ${
                    attachmentMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {attachmentMessage.text}
                </div>
              )}

              {attachments.length > 0 && (
                <div className="space-y-2 mb-4">
                  {attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(att.fileSize)} &middot; {new Date(att.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={engagementService.downloadAttachmentUrl(id, att.id)}
                          className="text-accent hover:text-accent-dark text-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(att.id, att.filename)}
                          className="text-red-400 hover:text-red-600 text-sm"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2">
                  <label className="btn btn-secondary text-sm cursor-pointer inline-flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {uploading ? 'Uploading...' : 'Upload File'}
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip"
                    />
                  </label>
                  {uploading && (
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Max 10MB per file. Accepted: PDF, Word, Excel, PowerPoint, images, text, CSV, ZIP.
                </p>
              </div>
            </div>

            {/* Time Entries */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Time Entries</h2>
                <button 
                  className="btn btn-secondary text-sm"
                  onClick={() => setShowTimeForm(true)}
                >
                  + Log Time
                </button>
              </div>

              {/* Time Entry Form */}
              {showTimeForm && (
                <form onSubmit={handleTimeFormSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Date *</label>
                      <input
                        type="date"
                        name="date"
                        value={timeFormData.date}
                        onChange={handleTimeFormChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Hours *</label>
                      <input
                        type="number"
                        name="hours"
                        value={timeFormData.hours}
                        onChange={handleTimeFormChange}
                        className="form-input"
                        step="0.25"
                        min="0.25"
                        placeholder="e.g., 2.5"
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="billable"
                          checked={timeFormData.billable}
                          onChange={handleTimeFormChange}
                          className="rounded border-gray-300 text-accent focus:ring-accent"
                        />
                        <span className="text-sm text-gray-700">Billable</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Description *</label>
                    <textarea
                      name="description"
                      value={timeFormData.description}
                      onChange={handleTimeFormChange}
                      className="form-input"
                      rows={2}
                      placeholder="What did you work on?"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button type="button" onClick={cancelTimeForm} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={savingTime}>
                      {savingTime ? 'Saving...' : editingTimeEntry ? 'Update Entry' : 'Log Time'}
                    </button>
                  </div>
                </form>
              )}

              {/* Time Entries List */}
              {timeEntries.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {timeEntries.map((entry) => (
                    <div key={entry.id} className="py-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{entry.description || 'Work performed'}</span>
                            {!entry.billable && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Non-billable</span>
                            )}
                            {entry.billed && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Billed</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(entry.date)} • {entry.hours} hrs
                            {entry.billable && entry.rate && ` @ ${formatCurrency(entry.rate)}/hr`}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {entry.billable && !entry.billed && (
                            <span className="text-sm font-medium text-accent">
                              {formatCurrency(entry.hours * (entry.rate || 0))}
                            </span>
                          )}
                          {!entry.billed && (
                            <>
                              <button
                                onClick={() => handleEditTimeEntry(entry)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteTimeEntry(entry.id)}
                                className="text-gray-400 hover:text-red-600"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm py-4">No time entries yet. Click "Log Time" to add your first entry.</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Time Summary */}
            <div className="card bg-accent/5 border-accent/20">
              <h2 className="text-lg font-semibold mb-4">Time Summary</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Total Hours</dt>
                  <dd className="font-medium">{totalHours.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Billable Hours</dt>
                  <dd className="font-medium">{billableHours.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <dt className="text-sm text-gray-600">Unbilled Hours</dt>
                  <dd className="font-medium text-accent">{unbilledHours.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Unbilled Amount</dt>
                  <dd className="font-semibold text-lg text-accent">{formatCurrency(unbilledAmount)}</dd>
                </div>
              </dl>
              {unbilledHours > 0 && (
                <Link 
                  to={`/invoices/new?engagement=${id}&client=${engagement.client?.id}`}
                  className="btn btn-primary w-full mt-4"
                >
                  Create Invoice
                </Link>
              )}
            </div>

            {/* Billing Summary */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Billing</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Rate Type</dt>
                  <dd className="font-medium capitalize">{engagement.rateType || 'Hourly'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">
                    {engagement.rateType === 'hourly' ? 'Hourly Rate' : 
                     engagement.rateType === 'retainer' ? 'Monthly Retainer' : 'Rate'}
                  </dt>
                  <dd className="font-medium">{formatCurrency(engagement.rate)}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Budget / Contract Value</dt>
                  <dd className="font-medium text-lg text-accent">{formatCurrency(engagement.budget)}</dd>
                </div>
              </dl>
            </div>

            {/* Timeline */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Timeline</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Start Date</dt>
                  <dd className="font-medium">{formatDate(engagement.startDate)}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">End Date</dt>
                  <dd className="font-medium">{formatDate(engagement.endDate)}</dd>
                </div>
              </dl>
            </div>

            {/* Related Proposal */}
            {engagement.proposal && (
              <div className="card">
                <h2 className="text-lg font-semibold mb-4">Related Proposal</h2>
                <Link 
                  to={`/proposals/${engagement.proposal.id || engagement.proposal}`}
                  className="text-accent hover:underline"
                >
                  View Original Proposal →
                </Link>
              </div>
            )}

            {/* Metadata */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Details</h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Engagement ID</dt>
                  <dd className="font-mono text-gray-900">#{engagement.id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Created</dt>
                  <dd className="text-gray-900">{formatDate(engagement.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last Updated</dt>
                  <dd className="text-gray-900">{formatDate(engagement.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
